/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo, useState, VFC } from 'react';
import { Button, Col, DatePicker, Form, Row, Select, Space, Spin, Table, Tag, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ProfileOutlined, SearchOutlined } from '@ant-design/icons';
import { useModal } from '@ebay/nice-modal-react';
import { Moment } from 'moment';
import { useAuth } from '@isocel/front-common';

import { Invoice, Store } from '../../queries/api/types';
import { useStores } from '../../queries/store';
import { getInvoices } from '../../queries/api/invoice';
import InvoiceDetailsDrawer, { formatDate } from './InvoiceDetailsDrawer';

import './HistoriqueFacturation.less';

const { Option } = Select;
const SELECT_ALL_VALUE = '__ALL__';

const HistoriqueFacturation: VFC = () => {
    const { user } = useAuth();
    const organizationId = user?.organization?.id;

    // --- Filters ---
    const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
    const [storeSearch, setStoreSearch] = useState('');
    const [startDate, setStartDate] = useState<Moment | null>(null);
    const [endDate, setEndDate] = useState<Moment | null>(null);

    // --- Data ---
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);

    const invoiceDetailsDrawer = useModal(InvoiceDetailsDrawer);

    const { data: storesData, isLoading: storesLoading } = useStores();

    const stores: Store[] = useMemo(() => {
        const all = storesData ?? [];
        if (!organizationId) return all;
        return all.filter((s) => {
            const orgId =
                typeof s.organization === 'string'
                    ? s.organization
                    : (s.organization as any)?.id ?? (s.organization as any)?._id;
            return orgId === organizationId;
        });
    }, [storesData, organizationId]);

    const filteredStores = useMemo(() => {
        const q = storeSearch.trim().toLowerCase();
        const list = q
            ? stores.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q))
            : stores;
        return [...list].sort((a, b) => {
            const numA = parseInt(a.code, 10);
            const numB = parseInt(b.code, 10);
            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
            return a.code.localeCompare(b.code);
        });
    }, [stores, storeSearch]);

    const allFilteredSelected =
        filteredStores.length > 0 && filteredStores.every((s) => selectedStoreIds.includes(s.id));

    const handleStoresChange = (values: string[]) => {
        if (values.includes(SELECT_ALL_VALUE)) {
            const allIds = filteredStores.map((s) => s.id);
            const alreadySelected = allIds.length > 0 && allIds.every((id) => selectedStoreIds.includes(id));
            setSelectedStoreIds(alreadySelected ? [] : allIds);
        } else {
            setSelectedStoreIds(values);
        }
    };

    // Both dates are optional.
    const disabledEndDate = useCallback(
        (current: Moment | null) => {
            if (!current || !startDate) return false;
            return current.isBefore(startDate, 'day');
        },
        [startDate]
    );

    const handleStartDateChange = (value: Moment | null) => {
        setStartDate(value);
        if (value && endDate && endDate.isBefore(value, 'day')) {
            setEndDate(null);
        }
    };

    const handleSearch = async () => {
        if (!organizationId) return;
        setLoading(true);
        setSearched(true);
        try {
            const data = await getInvoices({
                organizationId,
                storeIds: selectedStoreIds,
                startDate: startDate ? startDate.clone().startOf('day').toISOString() : null,
                endDate: endDate ? endDate.clone().endOf('day').toISOString() : null,
            });
            setInvoices(data);
        } catch (err: any) {
            const detail = err?.response?.data?.message || err?.response?.data || err?.message || String(err);
            message.error(
                `Erreur lors de la recherche des factures : ${
                    typeof detail === 'string' ? detail : JSON.stringify(detail)
                }`
            );
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setSelectedStoreIds([]);
        setStartDate(null);
        setEndDate(null);
        setInvoices([]);
        setSearched(false);
    };

    const columns: ColumnsType<Invoice> = [
        {
            title: 'Numéro lot facturation',
            dataIndex: 'name',
            key: 'name',
            render: (value: string, r) => value || r._id,
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        },
        {
            title: 'Date complète déclenchement',
            dataIndex: 'createdAt',
            key: 'createdAt',
            defaultSortOrder: 'descend',
            sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            render: (value: string) => formatDate(value, true),
        },
        {
            title: 'Date début',
            dataIndex: 'startDate',
            key: 'startDate',
            render: (value: string) => formatDate(value),
            sorter: (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
        },
        {
            title: 'Date fin',
            dataIndex: 'endDate',
            key: 'endDate',
            render: (value: string) => formatDate(value),
            sorter: (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
        },
        {
            title: 'Utilisateur',
            key: 'user',
            render: (_: unknown, r) => r.userName || r.userId || '-',
        },
        {
            title: 'Statut',
            dataIndex: 'status',
            key: 'status',
            render: (value?: string) =>
                value ? <Tag className="historique-facturation-status">{value}</Tag> : <Tag>-</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, invoice: Invoice) => (
                <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                        invoiceDetailsDrawer.show({ data: { invoice } }).catch(() => undefined);
                    }}
                >
                    Détails
                </Button>
            ),
        },
    ];

    return (
        <div className="historique-facturation-page">
            <h1>Historique de facturation</h1>

            <div className="historique-facturation-filters">
                <Form layout="vertical">
                    <Row gutter={16}>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item label="Magasin" className="bold-label">
                                <Select
                                    mode="multiple"
                                    showSearch
                                    placeholder="Sélectionner un ou plusieurs magasins"
                                    onSearch={setStoreSearch}
                                    filterOption={false}
                                    maxTagCount="responsive"
                                    suffixIcon={<SearchOutlined />}
                                    notFoundContent={storesLoading ? <Spin size="small" /> : 'Aucun résultat'}
                                    value={selectedStoreIds}
                                    onChange={handleStoresChange}
                                    onDropdownVisibleChange={(open) => {
                                        if (!open) setStoreSearch('');
                                    }}
                                >
                                    <Option key={SELECT_ALL_VALUE} value={SELECT_ALL_VALUE}>
                                        {allFilteredSelected ? 'Tout désélectionner' : 'Sélectionner tous les magasins'}
                                    </Option>
                                    {filteredStores.map((store) => (
                                        <Option key={store.id} value={store.id}>
                                            {store.code} - {store.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={6}>
                            <Form.Item label="Date d'émission - début" className="bold-label">
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    placeholder="Date de début"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={6}>
                            <Form.Item label="Date d'émission - fin" className="bold-label">
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    placeholder="Date de fin"
                                    value={endDate}
                                    onChange={setEndDate}
                                    disabledDate={disabledEndDate}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Space>
                                <Button
                                    type="primary"
                                    onClick={handleSearch}
                                    loading={loading}
                                    disabled={!organizationId}
                                >
                                    Rechercher
                                </Button>
                                <Button onClick={handleReset} disabled={loading}>
                                    Réinitialiser
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </div>

            {searched && (
                <div className="historique-facturation-results">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <ProfileOutlined style={{ fontSize: 20, color: '#0066cc' }} />
                        <h3 style={{ margin: 0 }}>Factures ({invoices.length})</h3>
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 24 }}>
                            <Spin />
                        </div>
                    ) : invoices.length > 0 ? (
                        <Table
                            columns={columns}
                            dataSource={invoices}
                            rowKey={(r) => r._id}
                            pagination={{ pageSize: 20 }}
                            bordered
                            size="middle"
                        />
                    ) : (
                        <p className="historique-facturation-empty">
                            Aucune facture ne correspond aux filtres sélectionnés.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default HistoriqueFacturation;
